import type { ParseReport, PromotionHold, PromotionHoldReason } from '@/services/ingestion'

/**
 * Every reason a record is held out of the pipeline (Bible 14 #7, ENT-12).
 *
 * A record can be held for more than one reason at once: an email that lists
 * several deals AND names its deal from a subject-line fragment is held for
 * both. The server keeps the first reason in `promotionHold`'s own fields (every
 * reader keyed on `promotionHold.reason` still works) and every active reason
 * in `promotionHold.reasons`. This reads the list — and, for a hold stored before
 * reasons accumulated, the one reason it carries.
 */

/** The hold the server writes on mail read from the mailbox's Junk folder. */
export const JUNK_HOLD = 'filed as Junk by the mail server'
/** The hold on a deal name the engine is not confident of. */
export const NAME_HOLD = 'deal name not confirmed'
/** The hold on an email that lists several deals. */
export const SEVERAL_DEALS_HOLD = 'several deals in one email'

export function holdReasons(hold: PromotionHold | null | undefined): PromotionHoldReason[] {
  if (!hold) return []
  const { reasons, ...own } = hold
  // The hold's own fields are the first reason and win over the list's copy of it.
  const first: PromotionHoldReason[] = own.reason ? [own as PromotionHoldReason] : []
  const rest = (reasons ?? []).filter((r) => r && r.reason && r.reason !== own.reason)
  const seen = new Set<string>()
  return [...first, ...rest].filter((r) => (seen.has(r.reason) ? false : (seen.add(r.reason), true)))
}

export function holdReasonNames(report: Pick<ParseReport, 'promotionHold'> | null | undefined): string[] {
  return holdReasons(report?.promotionHold).map((r) => r.reason)
}

export function isHeldFor(report: Pick<ParseReport, 'promotionHold'> | null | undefined, reason: string): boolean {
  return holdReasonNames(report).includes(reason)
}
